import { type PersonTopic, type Topic } from "./model";

// The GLOBAL curated domain catalogue: the STRAD investment landscape, available
// to all users and packs (not tied to one). `group` and optional `subgroup` are
// PRESENTATION ONLY: they organise the picker and are NEVER written to a tag. A
// person's flag stays flat (one normalised key); the catalogue spelling is what
// keys against the vocabulary. No hierarchy is stored or enforced.
export interface DomainCatalogueEntry {
  name: string;
  group: string;
  subgroup?: string;
}

function cat(group: string, names: string[], subgroup?: string): DomainCatalogueEntry[] {
  return names.map((name) => (subgroup ? { name, group, subgroup } : { name, group }));
}

export const DOMAIN_CATALOGUE: DomainCatalogueEntry[] = [
  ...cat("Private equity", [
    "Buyout", "Growth equity", "Venture capital", "PE secondaries",
    "Co-investment", "GP stakes", "Special situations equity",
  ]),
  ...cat("Private credit", [
    "Direct lending", "Mezzanine", "Distressed debt", "Special situations credit",
    "Structured credit", "Asset-based lending", "Real estate debt",
    "Infrastructure debt", "Credit secondaries",
  ]),
  ...cat("Real assets", [
    "Infrastructure", "Digital infrastructure", "Real estate (equity)",
    "Natural resources", "Timberland & farmland", "Transport & logistics assets",
    "Utilities & power",
  ]),
  // The P5 energy-transition terms fold in here, keeping their original spelling
  // so existing flags resolve to the same normalised key (no duplication).
  ...cat(
    "Real assets",
    [
      "Solar", "Onshore wind", "Offshore wind", "Battery storage",
      "Grid / transmission", "Hydrogen", "EV / charging", "Hydro", "Biomass",
      "CCS", "Geothermal", "Energy efficiency",
    ],
    "Energy transition",
  ),
  ...cat(
    "Real assets",
    [
      "Residential / multifamily", "Logistics & industrial", "Office", "Retail",
      "Hospitality", "Healthcare real estate", "Student & senior housing",
      "Data centres", "Self-storage",
    ],
    "Real estate sectors",
  ),
  ...cat("Hedge funds / liquid alts", [
    "Long/short equity", "Global macro", "Relative value", "Event-driven",
    "Merger arbitrage", "Managed futures / CTA", "Multi-strategy", "Credit hedge",
    "Systematic / quant", "Volatility / tail risk",
  ]),
  ...cat("Alternatives — other", [
    "Secondaries (broad)", "Fund-of-funds", "Insurance-linked / cat bonds",
    "Litigation finance", "Royalties & IP", "Digital assets / crypto",
    "Collectibles & art",
  ]),
  ...cat("Equities", [
    "Global equities", "US equities", "European equities", "UK equities",
    "Japan equities", "EM equities", "China equities", "Asia ex-Japan",
    "Frontier markets", "Small cap", "Value", "Growth", "Quality",
    "Dividend / income", "Passive / index", "Quant equity",
  ]),
  ...cat("Fixed income", [
    "Sovereign / government bonds", "Investment-grade credit", "High yield",
    "EM debt", "Inflation-linked", "Municipals", "Money market / cash",
    "Convertibles",
  ]),
  ...cat("Multi-asset & other", [
    "Multi-asset / balanced", "Commodities", "Currencies / FX", "Precious metals",
  ]),
  ...cat("Themes & sectors", [
    "Technology & software", "AI & compute", "Semiconductors",
    "Healthcare & biotech", "Financials & fintech", "Consumer", "Industrials",
    "Materials & mining", "Telecoms & media", "Agriculture & food",
    "Climate & decarbonisation", "ESG & impact", "Defence & security", "Space",
  ]),
];

// Type-ahead over the catalogue: match the term, its group, or its subgroup, so
// "credit" surfaces the Private credit group and "wind" the two wind terms.
export function searchCatalogue(query: string): DomainCatalogueEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return DOMAIN_CATALOGUE;
  return DOMAIN_CATALOGUE.filter(
    (e) =>
      e.name.toLowerCase().includes(q) ||
      e.group.toLowerCase().includes(q) ||
      (e.subgroup?.toLowerCase().includes(q) ?? false),
  );
}

export interface CatalogueGroup {
  group: string;
  sections: { subgroup?: string; entries: DomainCatalogueEntry[] }[];
}

// Group entries for the collapsible picker, preserving catalogue order.
export function groupCatalogue(entries: DomainCatalogueEntry[]): CatalogueGroup[] {
  const groups: CatalogueGroup[] = [];
  for (const e of entries) {
    let g = groups.find((x) => x.group === e.group);
    if (!g) {
      g = { group: e.group, sections: [] };
      groups.push(g);
    }
    let s = g.sections.find((x) => x.subgroup === e.subgroup);
    if (!s) {
      s = { subgroup: e.subgroup, entries: [] };
      g.sections.push(s);
    }
    s.entries.push(e);
  }
  return groups;
}

// Tokenise to spaces so matching is word-bounded: "CCS" never matches inside
// "success", and a multi-word domain ("offshore wind") matches only when its
// words are adjacent. Slashes and punctuation become separators.
function canon(s: string): string {
  return ` ${s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/\s+/g, " ")} `;
}

// Which domains appear, by name, in a free-text decision prompt. This is what
// lets Assemble accept a domain in the prompt ("credible buyers for an offshore
// wind portfolio") and use it as a filter over the dimension ranking.
export function domainsInText(topics: Topic[], text: string): Topic[] {
  const hay = canon(text);
  return topics.filter((t) => t.name.trim() !== "" && hay.includes(canon(t.name)));
}

// The domains actually in use (flagged on at least one person), with a count,
// for the cross-view domain filter. Keyed by topic id, sorted by name.
export function domainsPresent(
  topics: Topic[],
  personTopics: PersonTopic[],
): { key: string; label: string; count: number }[] {
  const count = new Map<string, number>();
  for (const pt of personTopics) count.set(pt.topic_id, (count.get(pt.topic_id) ?? 0) + 1);
  return topics
    .filter((t) => count.has(t.id))
    .map((t) => ({ key: t.id, label: t.name, count: count.get(t.id)! }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// The set of people flagged with ANY of the given domains. Intersect this with
// the dimension ranking; never fuse the two axes.
export function peopleMatchingDomains(
  personTopics: PersonTopic[],
  topicIds: string[],
): Set<string> {
  const wanted = new Set(topicIds);
  const people = new Set<string>();
  for (const pt of personTopics) {
    if (wanted.has(pt.topic_id)) people.add(pt.person_id);
  }
  return people;
}
